import React from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Input } from '@components/atoms/Input';

export interface StandaloneLineItemDraft {
  id: string;
  description: string;
  quantity: number | '';
  unitPrice: number | '';
}

interface StandaloneInvoiceLineItemRowProps {
  item: StandaloneLineItemDraft;
  currency?: string;
  disabled?: boolean;
  canRemove?: boolean;
  onChange: (patch: Partial<StandaloneLineItemDraft>) => void;
  onRemove: () => void;
}

const formatCurrency = (amount: number, currency = 'USD'): string => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

/**
 * A single free-form invoice line item (description, quantity, unit price) with a
 * live line-total and a remove control. Built from Input atoms — used by the
 * super-admin standalone invoice page.
 */
export const StandaloneInvoiceLineItemRow: React.FC<StandaloneInvoiceLineItemRowProps> = ({
  item,
  currency = 'USD',
  disabled = false,
  canRemove = true,
  onChange,
  onRemove,
}) => {
  const quantity = Number(item.quantity) || 0;
  const unitPrice = Number(item.unitPrice) || 0;
  const lineTotal = Math.round((quantity * unitPrice + Number.EPSILON) * 100) / 100;

  return (
    <div className="grid grid-cols-1 gap-3 border-b border-gray-100 py-3 dark:border-gray-700 sm:grid-cols-12 sm:items-start sm:gap-2">
      <div className="sm:col-span-6">
        <label className="mb-1 block text-xs font-medium text-gray-500 sm:hidden">Description</label>
        <Input
          type="text"
          placeholder="Description"
          value={item.description}
          disabled={disabled}
          onChange={(e) => onChange({ description: e.target.value })}
        />
      </div>

      <div className="sm:col-span-2">
        <label className="mb-1 block text-xs font-medium text-gray-500 sm:hidden">Quantity</label>
        <Input
          type="number"
          min={0}
          step="any"
          placeholder="Qty"
          value={item.quantity}
          disabled={disabled}
          onChange={(e) => onChange({ quantity: e.target.value === '' ? '' : Number(e.target.value) })}
        />
      </div>

      <div className="sm:col-span-2">
        <label className="mb-1 block text-xs font-medium text-gray-500 sm:hidden">Unit price</label>
        <Input
          type="number"
          min={0}
          step="any"
          placeholder="Unit price"
          value={item.unitPrice}
          disabled={disabled}
          onChange={(e) => onChange({ unitPrice: e.target.value === '' ? '' : Number(e.target.value) })}
        />
      </div>

      <div className="flex items-center justify-between sm:col-span-2 sm:justify-end sm:gap-2 sm:pt-2">
        <span className="text-sm font-medium text-gray-900 dark:text-white">
          {formatCurrency(lineTotal, currency)}
        </span>
        <button
          type="button"
          aria-label="Remove line item"
          disabled={disabled || !canRemove}
          onClick={onRemove}
          className="text-gray-400 transition-colors hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <DeleteOutlineIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
};

export default StandaloneInvoiceLineItemRow;
